/**
 * Capacitor plugin for sharing content in any sharing-enabled apps the user may have installed.
 * Reference docs used to derive types & behavior:
 *   https://www.npmjs.com/package/@capacitor/share
 */

import { CapacitorPluginsBase } from "./capacitorPluginsBase";
import fileHelper from "./fileHelper";
import Notification from "./notification";
import { Directory } from "./types";
import * as datadog from "@/shared/datadog";

class Share extends CapacitorPluginsBase {
/** Show a Share modal for sharing content with other apps
 * @param url - full url of a file to share
 */
  async shareUrl (url: string) {
    try {
      const { value: canShare } = await this.plugins.Share.canShare();
      if (!canShare) {
        datadog.log("Mobile share failed. Share functionality is not supported on the device.", {}, "info");
        Notification.alert({
          message: "Share functionality is not supported.",
          title: "Unable to share!",
          buttonName: "OK"
        });
        return;
      }
      const downloadFileResult = await fileHelper.downloadFile({ url, directory: Directory.Documents, trackProgress: false });
      await this.plugins.Share.share({ url: downloadFileResult.path });
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes("canceled")) {
          return Promise.resolve();
        }
      }
      datadog.log("Mobile share error.", err, "error");
      throw err;
    }
  }

  /**
   * Show a share modal for sharing a file with other apps.
   * Defaults to downloading the file if the device doesn't support sharing.
   * @param url - Full URL of a file to share.
   */
  async shareUrlForDownload (url: string) {
    try {
      const { value: canShare } = await this.plugins.Share.canShare();
      const downloadOptions = { url, directory: Directory.Documents, trackProgress: !canShare };
      const downloadFileResult = await fileHelper.downloadFile(downloadOptions);

      if (!canShare) {
        return;
      }

      await this.plugins.Share.share({
        url: downloadFileResult.path,
        dialogTitle: "Save form"
      });
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes("canceled")) {
          return Promise.resolve();
        }
      }
      datadog.log("Mobile share error.", err, "error");
      throw err;
    }
  }
}

export default new Share();